import type { ConfigurationEncode } from "../security/configurationEncode";
import { CryptoHelper } from "../security/cryptoHelper";

export interface UserCredentials {
  userId: number;
  firstName: string;
  lastName: string;
  fullName: string;
  areaCode?: string;
  phoneNumber?: string;
  userState: number;
  profileId: number;
  isMouchiSpecialCustomer?: boolean;
}

export interface UserStorageOptions {
  storageKey: string;
  encrypt?: boolean;
  encoder?: ConfigurationEncode;
}

const DEFAULT_ENCODER: ConfigurationEncode = {
  key: "KEYencodeECMABCDEFGHSAGIJKLMNCore",
  keyLength: 32,
  iv: "IVencodeECMABCDEF",
  ivLength: 16,
};

export const createUserStorage = (options: UserStorageOptions) => {
  const encryptUser = options.encrypt !== false;
  const encoder = options.encoder ?? DEFAULT_ENCODER;

  const serialize = (value: UserCredentials) => {
    const json = JSON.stringify(value);
    return encryptUser ? CryptoHelper.encrypt(encoder, json) : json;
  };

  const deserialize = (raw: string) => {
    if (!encryptUser) {
      return JSON.parse(raw) as UserCredentials;
    }
    const decrypted = CryptoHelper.decrypt(encoder, raw);
    return JSON.parse(decrypted) as UserCredentials;
  };

  const save = (data: UserCredentials) => {
    localStorage.setItem(options.storageKey, serialize(data));
  };

  const updateProfile = (updated: {
    nombre: string | null;
    apellido: string | null;
    apellidoNombre: string | null;
    codigoArea: string | null;
    telefono: string | null;
    fechaNacimiento?: string | null;
  }) => {
    const current = get();
    if (!current) return;

    const newData: UserCredentials = {
      ...current,
      firstName: updated.nombre ?? current.firstName,
      lastName: updated.apellido ?? current.lastName,
      fullName: updated.apellidoNombre ?? current.fullName,
      areaCode: updated.codigoArea ?? current.areaCode,
      phoneNumber: updated.telefono ?? current.phoneNumber,
    };

    localStorage.setItem(options.storageKey, serialize(newData));
  };

  const get = (): UserCredentials | null => {
    const raw = localStorage.getItem(options.storageKey);
    if (!raw) return null;

    try {
      return deserialize(raw);
    } catch {
      clear();
      return null;
    }
  };

  const clear = () => {
    localStorage.removeItem(options.storageKey);
  };

  return {
    save,
    updateProfile,
    get,
    clear,
    isLogged: () => !!get(),
    getUserId: () => get()?.userId,
    getFullName: () => get()?.fullName,
    getAreaCode: () => get()?.areaCode,
    getPhoneNumber: () => get()?.phoneNumber,
    isMouchiSpecialCustomer: () => get()?.isMouchiSpecialCustomer === true,
  };
};

